import { memo } from 'react';
import { Box } from '@mui/material';
import { TransformedFacilityValues } from 'benchmarking-domain/kpi-value';
import { ChartData, ChartOptions, Point } from 'chart.js';
import { tooltipTitle } from 'config/chart-js.config';
import { Bar } from 'react-chartjs-2';

import { KpiDetailsProps } from './kpi-details';
import { formatValueForChart } from './value-formater';

const KpiChartDistribution: React.FC<KpiDetailsProps> = memo(({ project, scopeKpiValues, kpi, statistics }) => {
  const statisticsData: TransformedFacilityValues = statistics?.get(kpi.id)?.transformedFaciliyValues || {};
  const sortedData = Object.entries(statisticsData).sort(([, a], [, b]) => a.value - b.value);

  if (sortedData.length == 0) {
    return (
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        No data to display
      </Box>
    );
  }

  const currentKpiFacilityId = kpi.dynamicKpiParentName
    ? `${project.plantId}@${kpi.dynamicKpiParentName}`
    : project.plantId;

  const isBenchmarkedFacility = (facilityId: string) => {
    return currentKpiFacilityId === facilityId;
  };

  const plantNames = sortedData.map(([facilityId], index) => {
    if (isBenchmarkedFacility(facilityId)) {
      return ['Benchmarked', 'Facility'];
    }

    return `Facility ${index + 1}`;
  });

  const middleIndex = Math.ceil(sortedData.length / 2);
  const lastIndex = sortedData.length - 1;

  const getData = (
    condition: (id: string, value: { isCurrentOrganisation: boolean; value: number }) => boolean
  ): Point[] => {
    return sortedData
      .map(([_id, _value], _index) => (condition(_id, _value) ? { x: _index, y: _value.value } : null))
      .filter((x) => x != null) as Point[];
  };

  const data: ChartData<'bar'> = {
    labels: plantNames,
    datasets: [
      {
        label: 'Benchmarked Facility',
        data: getData((id) => isBenchmarkedFacility(id)) as any,
        backgroundColor: '#2e96ff',
      },
      {
        label: 'Current Organization',
        data: getData((id, value) => !isBenchmarkedFacility(id) && value.isCurrentOrganisation),
        backgroundColor: '#02b2af',
      },
      {
        label: 'Outside Organization',
        data: getData((_, value) => !value.isCurrentOrganisation),
        backgroundColor: '#f0f0f0',
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: -1,
        max: sortedData.length,
        type: 'linear',
        offset: false,
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        title: {
          display: true,
          text: 'Facilities',
        },
      },
      y: {
        stacked: true,
      },
    },
    interaction: {
      axis: 'x',
      mode: 'nearest',
      intersect: false,
    },
    layout: {
      padding: {
        top: 30,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      datalabels: {
        display: (ctx) => {
          const point = ctx.chart.data.datasets[ctx.datasetIndex].data[ctx.dataIndex] as Point;
          const xValue = point?.x;
          return sortedData.length > 0 && [0, middleIndex, lastIndex].includes(xValue);
        },
        formatter: (value) => formatValueForChart(value?.y, kpi, 4),
        color: '#212636',
        anchor: 'end',
        align: 'top',
        rotation: -20,
        clip: false,
      },
      tooltip: {
        boxPadding: 5,
        callbacks: {
          label: (item) => formatValueForChart(item.parsed.y, kpi, 4),
          title: tooltipTitle,
        },
      },
    },
  };

  return <Bar options={options} data={data} />;
});

export default KpiChartDistribution;
